import type { InventoryGateway } from '../application/inventoryGateway';
import type { AccessResult } from '../domain/access';
import type {
AuthorizationRequestResult,
FavoriteResult,
InventoryAuthority,
InventoryAuthorityResult,
InventoryItem,
InventoryMapRequest,
InventoryPageResult,
InventoryRequest,
InventoryTransaction,
InventoryZonePoint,
MediaObjectResult,
MediaUploadResult,
} from '../domain/inventory';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession {
  readonly accessToken: string;
  readonly expiresAt: number;
}

interface InventoryRpcRow {
  readonly listing_id: string;
  readonly property_id: string;
  readonly title: string;
  readonly transaction_type: InventoryTransaction;
  readonly listing_status: string;
  readonly visibility: string;
  readonly price: number | string;
  readonly currency: string;
  readonly sort_at: string;
  readonly external_ref: string | null;
  readonly property_type: string;
  readonly address_line1: string | null;
  readonly neighborhood: string | null;
  readonly city: string;
  readonly state: string;
  readonly bedrooms: number | null;
  readonly bathrooms: number | string | null;
  readonly parking_spaces: number | null;
  readonly built_area_m2: number | string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly agency_name: string;
  readonly is_favorite: boolean;
  readonly media_path: string | null;
  readonly total_count: number | string;
}


export function inventoryZoneGeoJson(
  zone: readonly InventoryZonePoint[] | null,
): Record<string, unknown> | null {
  if (zone === null || zone.length < 3) return null;

  const coordinates = zone.map((point) => [
    point.longitude,
    point.latitude,
  ]);
  const first = coordinates[0];
  const last = coordinates.at(-1);

  if (
    first !== undefined &&
    last !== undefined &&
    (first[0] !== last[0] || first[1] !== last[1])
  ) {
    coordinates.push([...first]);
  }

  return {
    type: 'Polygon',
    coordinates: [coordinates],
  };
}

function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Math.floor(Date.now() / 1000)
    ) {
      return null;
    }
    return parsed.accessToken;
  } catch {
    return null;
  }
}

function numberOrNull(value: number | string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function storageObjectPath(path: string): string {
  return path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

function extensionForMime(mime: string): string | null {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/avif': 'avif',
  };
  return map[mime] ?? null;
}

function mapRow(row: InventoryRpcRow): InventoryItem {
  return {
    listingId: row.listing_id,
    propertyId: row.property_id,
    title: row.title,
    transactionType: row.transaction_type,
    listingStatus: row.listing_status,
    visibility: row.visibility,
    price: Number(row.price),
    currency: row.currency.trim(),
    sortAt: row.sort_at,
    externalRef: row.external_ref,
    propertyType: row.property_type,
    addressLine1: row.address_line1,
    neighborhood: row.neighborhood,
    city: row.city,
    state: row.state,
    bedrooms: row.bedrooms,
    bathrooms: numberOrNull(row.bathrooms),
    parkingSpaces: row.parking_spaces,
    builtAreaM2: numberOrNull(row.built_area_m2),
    latitude: row.latitude,
    longitude: row.longitude,
    agencyName: row.agency_name,
    isFavorite: row.is_favorite,
    mediaPath: row.media_path,
  };
}

export class SupabaseInventoryGateway implements InventoryGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> {
    return this.accessGateway.resolveCurrentIdentity(signal);
  }

  signOut(signal?: AbortSignal): Promise<void> {
    return this.accessGateway.signOut(signal);
  }

  async loadInventory(
    request: InventoryRequest,
    signal?: AbortSignal,
  ): Promise<InventoryPageResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return {
        ok: false,
        message: 'La sesión segura terminó. Vuelve a ingresar.',
      };
    }

    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/rpc/inventory_page_v3`,
        {
          method: 'POST',
          headers: {
            apikey: publishableKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            p_query: request.query.trim() === '' ? null : request.query.trim(),
            p_include_sale: request.sale,
            p_include_rent: request.rent,
            p_include_active: request.active,
            p_include_reserved: request.reserved,
            p_include_draft: request.draft,
            p_zone: inventoryZoneGeoJson(request.zone),
            p_cursor_sort_at: request.cursor?.sortAt ?? null,
            p_cursor_listing_id: request.cursor?.listingId ?? null,
            p_limit: request.limit,
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return {
          ok: false,
          message: 'No fue posible consultar el inventario autorizado.',
        };
      }

      const rows = (await response.json()) as InventoryRpcRow[];
      const items = rows.map(mapRow);
      const last = items.at(-1) ?? null;
      const totalCount =
        rows.length === 0 ? 0 : Number(rows[0]?.total_count ?? 0);

      return {
        ok: true,
        page: {
          items,
          totalCount,
          nextCursor:
            rows.length === request.limit && last !== null
              ? { sortAt: last.sortAt, listingId: last.listingId }
              : null,
        },
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible conectar con el inventario de staging.',
      };
    }
  }

  async loadMapInventory(
    request: InventoryMapRequest,
    signal?: AbortSignal,
  ): Promise<InventoryPageResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return {
        ok: false,
        message: 'La sesión segura terminó. Vuelve a ingresar.',
      };
    }

    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/rpc/inventory_map_v3`,
        {
          method: 'POST',
          headers: {
            apikey: publishableKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            p_query: request.query.trim() === '' ? null : request.query.trim(),
            p_include_sale: request.sale,
            p_include_rent: request.rent,
            p_include_active: request.active,
            p_include_reserved: request.reserved,
            p_include_draft: request.draft,
            p_zone: inventoryZoneGeoJson(request.zone),
            p_west: request.west,
            p_south: request.south,
            p_east: request.east,
            p_north: request.north,
            p_limit: request.limit,
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return {
          ok: false,
          message: 'No fue posible consultar el inventario de esta zona.',
        };
      }

      const rows = (await response.json()) as InventoryRpcRow[];
      const items = rows.map(mapRow);
      const totalCount =
        rows.length === 0 ? 0 : Number(rows[0]?.total_count ?? 0);

      return {
        ok: true,
        page: {
          items,
          totalCount,
          nextCursor: null,
        },
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible actualizar el mapa para esta zona.',
      };
    }
  }

  async loadMediaObject(
    mediaPath: string,
    signal?: AbortSignal,
  ): Promise<MediaObjectResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (supabaseUrl === undefined || publishableKey === undefined || accessToken === null) {
      return { ok: false, message: 'La sesión segura terminó. Vuelve a ingresar.' };
    }

    try {
      const response = await fetch(
        supabaseUrl + '/storage/v1/object/authenticated/listing-media/' + storageObjectPath(mediaPath),
        {
          headers: {
            apikey: publishableKey,
            Authorization: 'Bearer ' + accessToken,
          },
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return { ok: false, message: 'La imagen todavía no existe en Storage.' };
      }

      const blob = await response.blob();
      return { ok: true, objectUrl: URL.createObjectURL(blob) };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible leer la imagen desde Storage.' };
    }
  }

  async uploadPhoto(
    listingId: string,
    file: File,
    altText: string,
    signal?: AbortSignal,
  ): Promise<MediaUploadResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    const extension = extensionForMime(file.type);
    if (supabaseUrl === undefined || publishableKey === undefined || accessToken === null) {
      return { ok: false, message: 'La sesión segura terminó. Vuelve a ingresar.' };
    }

    if (extension === null || file.size < 1 || file.size > 6 * 1024 * 1024) {
      return {
        ok: false,
        message: 'Usa JPEG, PNG, WebP o AVIF de hasta 6 MB.',
      };
    }

    const mediaPath = listingId + '/' + crypto.randomUUID() + '.' + extension;
    const encodedPath = storageObjectPath(mediaPath);
    const headers = {
      apikey: publishableKey,
      Authorization: 'Bearer ' + accessToken,
    };

    try {
      const upload = await fetch(
        supabaseUrl + '/storage/v1/object/listing-media/' + encodedPath,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': file.type,
            'x-upsert': 'false',
          },
          body: file,
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!upload.ok) {
        return {
          ok: false,
          message: 'No fue posible guardar la imagen en Storage o no tienes permiso para ese listing.',
        };
      }

      const register = await fetch(
        supabaseUrl + '/rest/v1/listing_media',
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({
            listing_id: listingId,
            storage_bucket: 'listing-media',
            storage_path: mediaPath,
            media_type: 'image',
            sort_order: 0,
            alt_text: altText.trim() === '' ? null : altText.trim(),
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!register.ok) {
        await fetch(supabaseUrl + '/storage/v1/object/listing-media', {
          method: 'DELETE',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prefixes: [mediaPath] }),
        }).catch(() => undefined);
        return {
          ok: false,
          message: 'La imagen llegó a Storage, pero el registro fue rechazado por permisos o límites del plan.',
        };
      }

      return {
        ok: true,
        mediaPath,
        objectUrl: URL.createObjectURL(file),
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible completar la carga de imagen.' };
    }
  }

  async loadAuthority(
    listingId: string,
    signal?: AbortSignal,
  ): Promise<InventoryAuthorityResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return { ok: false, message: 'La sesión segura terminó. Vuelve a ingresar.' };
    }

    try {
      const response = await fetch(
        supabaseUrl + '/rest/v1/rpc/inventory_listing_authority_v1',
        {
          method: 'POST',
          headers: {
            apikey: publishableKey,
            Authorization: 'Bearer ' + accessToken,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ p_listing_id: listingId }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return { ok: false, message: 'No fue posible verificar la autoridad de esta propiedad.' };
      }

      const rows = (await response.json()) as {
        can_share: boolean;
        can_request: boolean;
        authority_state: InventoryAuthority['authorityState'];
        pending_request_id: string | null;
      }[];
      const row = rows[0];
      if (row === undefined) {
        return { ok: false, message: 'No fue posible verificar la autoridad de esta propiedad.' };
      }

      return {
        ok: true,
        authority: {
          canShare: row.can_share,
          canRequest: row.can_request,
          authorityState: row.authority_state,
          pendingRequestId: row.pending_request_id,
        },
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con la autoridad de la propiedad.' };
    }
  }

  async requestAuthorization(
    listingId: string,
    draftMessage: string | null,
    signal?: AbortSignal,
  ): Promise<AuthorizationRequestResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return { ok: false, message: 'La sesión segura terminó. Vuelve a ingresar.' };
    }

    try {
      const response = await fetch(
        supabaseUrl + '/rest/v1/rpc/dealboard_request_authorization_v1',
        {
          method: 'POST',
          headers: {
            apikey: publishableKey,
            Authorization: 'Bearer ' + accessToken,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            p_listing_id: listingId,
            p_message: draftMessage === null || draftMessage.trim() === ''
              ? null
              : draftMessage.trim(),
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return { ok: false, message: 'No fue posible crear la solicitud de autorización.' };
      }

      const payload = (await response.json()) as {
        request_id: string;
        workspace_id: string;
        property_vector_id: string;
        conversation_id?: string | null;
        draft_message?: string | null;
        message_sent: boolean;
      };

      if (payload.message_sent) {
        return { ok: false, message: 'La solicitud violó la regla de borrador no enviado.' };
      }

      return {
        ok: true,
        requestId: payload.request_id,
        workspaceId: payload.workspace_id,
        propertyVectorId: payload.property_vector_id,
        conversationId: payload.conversation_id ?? null,
        draftMessage: payload.draft_message ?? null,
        messageSent: false,
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con DealBoard.' };
    }
  }

  async setFavorite(
    listingId: string,
    favorite: boolean,
    signal?: AbortSignal,
  ): Promise<FavoriteResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();
    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return {
        ok: false,
        message: 'La sesión segura terminó. Vuelve a ingresar.',
      };
    }

    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/rpc/inventory_set_favorite_v1`,
        {
          method: 'POST',
          headers: {
            apikey: publishableKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            p_listing_id: listingId,
            p_favorite: favorite,
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        return {
          ok: false,
          message: 'No fue posible actualizar Favoritos.',
        };
      }

      return { ok: true, favorite };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible actualizar Favoritos.',
      };
    }
  }
}
